import React, { useState, useEffect, useRef } from 'react'
import { AiFillInfoCircle } from 'react-icons/ai'
import {
  useNavigate,
  useLocation,
  useParams,
  useSearchParams,
} from 'react-router-dom'
import VisaLogo1 from '../assets/images/backgrounds/verify-email.svg'
import Button from '../components/ui/button'
import Loader from '../components/ui/loader.jsx'
import { useLanguage } from '../context/LanguageContext'
import { useTenant } from '../context/TenantContext'
import apiService from '../services/api.service'

const VerifyEmail = () => {
  const { t, languageId } = useLanguage()
  const { tenant } = useTenant()
  const navigate = useNavigate()
  const location = useLocation()
  const { tenanturlname } = useParams()
  const [searchParams] = useSearchParams()

  // Get email from URL params (preferred) or location state (fallback)
  const emailFromUrl = searchParams.get('email')
  const emailFromState = location.state?.email
  const userEmail = emailFromUrl
    ? decodeURIComponent(emailFromUrl)
    : emailFromState

  const [isLoading, setIsLoading] = useState(false)
  const [isResent, setIsResent] = useState(false)
  const [error, setError] = useState(null)
  const [countdown, setCountdown] = useState(0)
  const [isLocked, setIsLocked] = useState(false)
  const lockoutTimerRef = useRef(null)

  // Redirect to signup if no email is available
  useEffect(() => {
    if (!userEmail) {
      navigate(`/${tenanturlname}/signup`)
    }
  }, [userEmail, navigate, tenanturlname])

  // Handle countdown timer
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1)
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [countdown])

  // Cleanup lockout timer on unmount
  useEffect(() => {
    return () => {
      if (lockoutTimerRef.current) {
        clearTimeout(lockoutTimerRef.current)
      }
    }
  }, [])

  const handleResendEmail = async () => {
    // Prevent multiple clicks during countdown, loading, or lockout
    if (countdown > 0 || isLoading || isLocked) {
      return
    }

    if (!userEmail) {
      setError('Email address is required')
      return
    }

    setIsLoading(true)
    setError(null)
    setIsResent(false)

    try {
      const response = await apiService.post(
        '/api/kc/resend-verification-email',
        {
          clientemail: userEmail,
          clienttenantid: tenant?.tenantid || 1011,
          defaultlanguageid: languageId || 490,
        },
      )

      setIsResent(true)

      // apiService.post returns response.data directly, so retry_after is at top level
      const retryAfter = response?.retry_after || 60
      setCountdown(Number(retryAfter))
    } catch (error) {
      console.error('Resend verification email error:', error)

      // Handle rate limit (429 from server or client-side)
      if (error.status === 429 || error.isRateLimit) {
        const reason = error.data?.reason

        if (reason === 'RESEND_ACCOUNT_LOCKED') {
          // Account locked — show locked message, disable button, NO countdown
          setError(
            t('verifyEmail.errors.accountLocked') ||
              'Your account is temporarily locked. Please try again later.',
          )
          setIsLocked(true)

          // Auto-re-enable button silently after lockout expires (no visible countdown)
          const lockRetryAfter = error.data?.retry_after
          if (lockRetryAfter) {
            if (lockoutTimerRef.current) {
              clearTimeout(lockoutTimerRef.current)
            }
            lockoutTimerRef.current = setTimeout(
              () => {
                setIsLocked(false)
                setError(null)
              },
              Number(lockRetryAfter) * 1000,
            )
          }
        } else {
          // Cooldown — show cooldown message with countdown timer
          const retryAfter = error.data?.retry_after || 60

          setError(
            t('verifyEmail.errors.cooldown') ||
              'Please wait before requesting another verification email.',
          )
          setCountdown(Number(retryAfter))
        }
      } else if (error.data?.message) {
        setError(error.data.message)
      } else {
        setError('Failed to resend verification email. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleReenterEmail = () => {
    navigate(`/${tenanturlname}/create-account`)
  }

  // Don't render if no email (will redirect)
  if (!userEmail) {
    return null
  }

  return (
    <>
      {isLoading && (
        <Loader
          fullscreen
          label={t('verifyEmail.resending') || 'Resending email...'}
          size="xl"
        />
      )}

      <div className="verify-email-page">
        {/* BANNER - START */}
        <div className="main-banner w-full max-w-[648px] mx-auto">
          <div className="w-full aspect-[648/381] overflow-hidden">
            {/* Mobile banner */}
            <img
              src={
                tenant?.tenantcontentconfig?.images?.verify_email
                  ? `${process.env.REACT_APP_CLOUDFRONT}tenantbannerimages/${tenant?.tenanturlname}/mob_${tenant.tenantcontentconfig.images.verify_email}`
                  : require('../assets/images/backgrounds/main-banner.png')
              }
              alt={t('common.bannerAlt')}
              className="block md:hidden w-full h-full object-contain border border-gray-300 rounded-md"
              loading="lazy"
              onError={(e) => {
                e.target.src = require('../assets/images/backgrounds/main-banner.png')
              }}
            />
            {/* Desktop banner */}
            <img
              src={
                tenant?.tenantcontentconfig?.images?.verify_email
                  ? `${process.env.REACT_APP_CLOUDFRONT}tenantbannerimages/${tenant?.tenanturlname}/web_${tenant.tenantcontentconfig.images.verify_email}`
                  : require('../assets/images/backgrounds/main-banner.png')
              }
              alt={t('common.bannerAlt')}
              className="hidden md:block w-full h-full object-contain border border-gray-300 rounded-md"
              loading="lazy"
              onError={(e) => {
                e.target.src = require('../assets/images/backgrounds/main-banner.png')
              }}
            />
          </div>
        </div>
        {/* BANNER - END */}

        {/* Disclaimer - START */}
        <div className="mt-4 px-4 w-full">
          <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4">
            <AiFillInfoCircle className="text-theme-primary shrink-0" size={16} />
            <p className="text-[12px] text-blue-700">
              {t('verifyEmail.disclaimer')}
            </p>
          </div>
        </div>
        {/* Disclaimer - END */}

        {/* Verify Email - START */}
        <div className="flex flex-col items-center max-w-md mx-auto mt-8 px-4">
          <div className="text-center mb-4">
            <h1 className="text-base font-extrabold text-theme-secondary">
              {t('verifyEmail.title') || t('signup.title')}
            </h1>
            <p className="text-xs text-theme-secondary mt-2">
              {t('verifyEmail.subtitle')}
            </p>
            <a
              href={`/${tenanturlname}/signin`}
              className="text-sm text-theme-primary hover:text-theme-primary/80 inline-block"
            >
              {t('verifyEmail.alreadyHaveAccount') ||
                t('signup.alreadyHaveAccount')}
            </a>
          </div>

          <img
            src={VisaLogo1}
            alt={t('verifyEmail.bannerAlt') || 'verify email logo'}
            className=""
          />

          <h2 className="mb-3 text-sm leading-7 font-bold text-theme-secondary">
            {t('verifyEmail.header')}
          </h2>

          <p className="mb-6 text-center text-xs font-normal text-theme-secondary max-w-[22rem]">
            {t('verifyEmail.instructions')}
          </p>

          {/* Display success message */}
          {isResent && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md w-full">
              <p className="text-sm text-green-600">
                {t('verifyEmail.resendSuccess') ||
                  'Verification email sent successfully. Please check your inbox.'}
              </p>
            </div>
          )}

          {/* Display error message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md w-full">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <Button
            id="resend-btn"
            variant="primary"
            size="lg"
            type="button"
            onClick={handleResendEmail}
            disabled={isLoading || isLocked || countdown > 0}
            isLoading={isLoading}
            className={
              isLocked || countdown > 0 ? 'opacity-60 cursor-not-allowed' : ''
            }
          >
            {isLocked
              ? t('verifyEmail.resendButton')
              : countdown > 0
                ? `${t('verifyEmail.resendButton')} (${countdown}s)`
                : t('verifyEmail.resendButton')}
          </Button>

          <button
            id="re-enter-email"
            onClick={handleReenterEmail}
            aria-label="Re-enter email"
            className="mt-4 bg-transparent border-none text-theme-primary text-sm font-semibold hover:underline cursor-pointer"
          >
            {t('verifyEmail.reenterEmail')}
          </button>
        </div>
        {/* Verify Email - END */}
      </div>
    </>
  )
}

export default VerifyEmail
